import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Leaf, Users, Target } from 'lucide-react';
import { useImages } from '../hooks/useImages';
import { SEO } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] } })
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

const values = [
  { icon: Heart, title: 'Women-Led', desc: 'Empowering women across the supply chain — from farm to fork.' },
  { icon: Leaf, title: 'Sustainable', desc: 'Chemical-free farming with AI-powered precision agriculture.' },
  { icon: Users, title: 'Community First', desc: 'Supporting 1000+ farmer families with fair trade practices.' },
  { icon: Target, title: 'AI-Powered', desc: 'Leveraging technology for smarter farming and efficient distribution.' },
];

const AboutUs = () => {
  const siteImages = useImages();
  const heroImg = siteImages['home-soil-farmer'] || 'https://images.unsplash.com/photo-1709552645252-f80c79dfb05d?crop=entropy&cs=srgb&fm=jpg&q=85';
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/timeline`).then(r => r.json()).then(d => {
      const sorted = d.sort((a, b) => a.date.localeCompare(b.date));
      setTimeline(sorted);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="about-page">
      <SEO title="About Us" description="Learn about Nistala's Group — a women-led, AI-powered farm-to-fork ecosystem empowering communities and transforming Indian agriculture." />
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="About Nistala's" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 to-[#2C1810]/50" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 text-center pt-16 pb-8">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-xs tracking-[0.25em] uppercase text-[#D4A574] mb-4">Our Story</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            About <span className="text-[#D4A574]">Nistala's Group</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="text-base text-white/80 max-w-2xl mx-auto leading-relaxed">
            A women-led, AI-powered farm-to-fork ecosystem rooted in India's heartland, committed to delivering pure, sustainable, and traceable food to every home.
          </motion.p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-10">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Our Mission</motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-base text-[#7a6555] max-w-3xl mx-auto leading-relaxed">
              To build a complete, transparent ecosystem that connects farmers directly to consumers — eliminating middlemen, ensuring fair prices, and delivering chemical-free food with full traceability. We believe every meal should tell a story of care, sustainability, and empowerment.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-12" style={{ background: '#FDF8F0' }}>
        <div className="max-w-5xl mx-auto px-4">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            What We Stand For
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {values.map((v, idx) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-xl p-6 border border-[#E8DFD3] shadow-sm"
              >
                <v.icon size={28} className="text-[#5C7A3A] mb-3" />
                <h3 className="text-lg font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{v.title}</h3>
                <p className="text-sm text-[#7a6555] leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      {timeline.length > 0 && (
        <section className="py-12 bg-white" data-testid="timeline-section">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-10">
              <motion.div variants={fadeUp} className="flex items-center gap-5 justify-center mb-3">
                <div className="h-px bg-gradient-to-r from-transparent to-[#D4A574] w-20" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#D4A574]" />
                <div className="h-px bg-gradient-to-l from-transparent to-[#D4A574] w-20" />
              </motion.div>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Nistala's Timeline
              </motion.h2>
            </motion.div>

            {/* Journey Map */}
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-6 sm:left-1/2 sm:-translate-x-px top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#D4A574] via-[#5C7A3A] to-[#D4A574]" />

              {timeline.map((event, idx) => {
                const isLeft = idx % 2 === 0;
                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: idx * 0.08 }}
                    className={`relative flex items-start mb-8 last:mb-0 ${isLeft ? 'sm:flex-row' : 'sm:flex-row-reverse'}`}
                    data-testid={`timeline-event-${idx}`}
                  >
                    {/* Dot on the line */}
                    <div className="absolute left-6 sm:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-white border-[3px] border-[#5C7A3A] z-10 mt-1.5" />

                    {/* Content card */}
                    <div className={`ml-14 sm:ml-0 sm:w-[calc(50%-2rem)] ${isLeft ? 'sm:pr-4 sm:text-right' : 'sm:pl-4 sm:text-left'}`}>
                      <span className="inline-block text-xs font-bold tracking-wider uppercase text-[#C4844A] bg-[#C4844A]/10 px-3 py-1 rounded-full mb-2">{event.date}</span>
                      <h3 className="text-lg font-bold text-[#2C1810] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{event.heading}</h3>
                      <p className="text-sm text-[#7a6555] leading-relaxed">{event.subheading}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Story */}
      <section className="py-12" style={{ background: timeline.length > 0 ? '#FDF8F0' : 'white' }}>
        <div className="max-w-4xl mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Our Journey</motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-base text-[#7a6555] leading-relaxed mb-4">
              Nistala's Group was born from a simple observation — the food on our plates had lost its connection to the soil it came from. Founded as a women-led initiative, we set out to rebuild that connection using the power of AI and community.
            </motion.p>
            <motion.p variants={fadeUp} custom={2} className="text-base text-[#7a6555] leading-relaxed mb-4">
              Incubated by UConn and WeHub, and supported by the U.S. Consulate, we've grown into a five-vertical ecosystem spanning organic products, agritech, cold-chain logistics, women empowerment, and culinary education.
            </motion.p>
            <motion.p variants={fadeUp} custom={3} className="text-base text-[#7a6555] leading-relaxed">
              Today, we work with over 1000 farmer families across India, ensuring every product that reaches your home is fresh, traceable, and grown with care.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-14 overflow-hidden">
        <div className="absolute inset-0 bg-[#2C1810]" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Join Our <span className="text-[#D4A574]">Movement</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="text-base text-white/70 mb-6 max-w-xl mx-auto">
            Whether you're a farmer, consumer, or partner — there's a place for you in the Nistala's ecosystem.
          </motion.p>
          <motion.a
            href="mailto:nistalas211221@gmail.com?subject=Partnership Inquiry"
            initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}
            className="inline-block bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-medium px-8 py-3 rounded-lg transition-colors"
          >
            Get in Touch
          </motion.a>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
